import type { AABB } from '../../geo/types';

/**
 * Items filed once, in their own order, under every cell of a square grid their bounds cross:
 * cell c holds items[start[c]] up to items[start[c + 1]]. Lot planning asks the fixed ground
 * hundreds of thousands of times, and flat arrays answer without a map lookup.
 */
export interface CellTable {
  readonly start: Int32Array;
  readonly items: Int32Array;
  readonly cellCount: number;
  /** The cell holding a point, or -1 off the table. */
  cellAt(x: number, y: number): number;
  /** The middle of a cell. */
  middle(cell: number): readonly [number, number];
  /** Every cell a box crosses, on the table. */
  forCells(box: AABB, visit: (cell: number) => void): void;
  /** Whether `test` holds for an item filed in a cell the box crosses; it may meet one twice. */
  some(box: AABB, test: (item: number) => boolean): boolean;
}

interface Extent {
  readonly minCx: number;
  readonly minCy: number;
  readonly cols: number;
  readonly rows: number;
}

function extentOf(bounds: readonly AABB[], size: number): Extent {
  if (bounds.length === 0) return { minCx: 0, minCy: 0, cols: 0, rows: 0 };
  let [minCx, minCy, maxCx, maxCy] = [Infinity, Infinity, -Infinity, -Infinity];
  for (const b of bounds) {
    minCx = Math.min(minCx, Math.floor(b.minX / size));
    minCy = Math.min(minCy, Math.floor(b.minY / size));
    maxCx = Math.max(maxCx, Math.floor(b.maxX / size));
    maxCy = Math.max(maxCy, Math.floor(b.maxY / size));
  }
  return { minCx, minCy, cols: maxCx - minCx + 1, rows: maxCy - minCy + 1 };
}

export function createCellTable(bounds: readonly AABB[], size: number): CellTable {
  const { minCx, minCy, cols, rows } = extentOf(bounds, size);
  const cells = cols * rows;
  const each = (visit: (cell: number, item: number) => void): void => {
    bounds.forEach((b, item) => {
      for (let cx = Math.floor(b.minX / size); cx <= Math.floor(b.maxX / size); cx++) {
        for (let cy = Math.floor(b.minY / size); cy <= Math.floor(b.maxY / size); cy++) {
          visit(cx - minCx + (cy - minCy) * cols, item);
        }
      }
    });
  };
  const start = new Int32Array(cells + 1);
  each((cell) => {
    start[cell + 1]!++;
  });
  for (let c = 0; c < cells; c++) start[c + 1]! += start[c]!;
  const next = start.slice(0, cells);
  const items = new Int32Array(start[cells]!);
  each((cell, item) => {
    items[next[cell]!++] = item;
  });

  const column = (x: number) => Math.floor(x / size) - minCx;
  const row = (y: number) => Math.floor(y / size) - minCy;

  const forCells = (box: AABB, visit: (cell: number) => void): void => {
    const cx1 = Math.min(cols - 1, column(box.maxX));
    const cy1 = Math.min(rows - 1, row(box.maxY));
    for (let cx = Math.max(0, column(box.minX)); cx <= cx1; cx++) {
      for (let cy = Math.max(0, row(box.minY)); cy <= cy1; cy++) visit(cx + cy * cols);
    }
  };

  return {
    start,
    items,
    cellCount: cells,
    forCells,
    middle(cell) {
      return [((cell % cols) + minCx + 0.5) * size, (Math.floor(cell / cols) + minCy + 0.5) * size];
    },
    cellAt(x, y) {
      const cx = column(x);
      const cy = row(y);
      return cx < 0 || cx >= cols || cy < 0 || cy >= rows ? -1 : cx + cy * cols;
    },
    some(box, test) {
      const cx1 = Math.min(cols - 1, column(box.maxX));
      const cy1 = Math.min(rows - 1, row(box.maxY));
      for (let cx = Math.max(0, column(box.minX)); cx <= cx1; cx++) {
        for (let cy = Math.max(0, row(box.minY)); cy <= cy1; cy++) {
          const cell = cx + cy * cols;
          for (let k = start[cell]!; k < start[cell + 1]!; k++) if (test(items[k]!)) return true;
        }
      }
      return false;
    },
  };
}
